import messagesApi from '@/common/api/message.api';
import toaster from '@/common/util/toaster';
import apiConfig from '@/config/config';
import config from '@/config/endpoints';
import { eventHub } from '@/main';
import router from '@/router';
import axios from 'axios';
import { jwtDecode } from 'jwt-decode';

export const axiosClient = axios.create({
	baseURL: config.endpoints.baseUrl,
	headers: {
		...(config.endpoints.defaults.headers || {}),
		clientId: process.env.VUE_APP_CLIENT_ID
	}
});

axiosClient.interceptors.request.use(
	async (config) => {
		// token de autorização
		try {
			const accessToken = sessionStorage.getItem('ACCESS_TOKEN');
			config.headers.common['Authorization'] = `Bearer ${accessToken}`;

			if (accessToken) {
				const now = Math.floor(Date.now() / 1000);
				const decodedToken = jwtDecode(accessToken);

				if (decodedToken.exp < now) {
					const { data: refreshToken } = await axios(
						`${apiConfig.apiURL}/api/session/refresh`,
						{
							method: 'POST',
							headers: {
								Authorization: `Bearer ${accessToken}`,
								'Content-Type': 'application/json',
								clientId: process.env.VUE_APP_CLIENT_ID,
							},
						}
					);
					sessionStorage.setItem('ACCESS_TOKEN', refreshToken);
					config.headers.common['Authorization'] = `Bearer ${refreshToken}`;
				}
			}

			eventHub.$emit('LOADING', true);
			return config;
		} catch (e) {
			eventHub.$emit('LOADING', false);
			console.log(e);
		}
	},
	(error) => {
		eventHub.$emit('LOADING', false);
		return Promise.reject(error);
	}
);

axiosClient.interceptors.response.use(
	(response) => {
		// token de autorização
		if (
			typeof response.data === 'object' &&
			response.data.data &&
			response.data.data.token &&
			response.data.data.token.access &&
			response.data.data.token.session
		) {
			sessionStorage.setItem('ACCESS_TOKEN', response.data.data.token.access);
			localStorage.setItem('REFRESH_TOKEN', response.data.data.token.session);
		}

		eventHub.$emit('LOADING', false);
		return response;
	},
	(error) => {
		eventHub.$emit('LOADING', false);

		// sessão expirada
		const originalRequest = error.config;

		if (error && error.response) {
			if (
				error.response.status === 419 &&
				!originalRequest._retry &&
				router.currentRoute.name !== 'AppLogin'
			) {
				originalRequest._retry = true;
				return refreshAccessToken()
					.then((accessToken) => {
						sessionStorage.setItem('ACCESS_TOKEN', accessToken);
						originalRequest.headers['Authorization'] = 'Bearer ' + accessToken;
						return axiosClient(originalRequest);
					})
					.catch(() => {
						sessionStorage.removeItem('ACCESS_TOKEN');
						localStorage.removeItem('REFRESH_TOKEN');
						router.push({ name: 'AppLogin' });

						return Promise.reject(error);
					});
			}
		}

		return Promise.reject(error);
	}
);

const refreshAccessToken = () => {
	const refreshToken = localStorage.getItem('REFRESH_TOKEN');
	const accessToken = sessionStorage.getItem('ACCESS_TOKEN');

	if (!refreshToken) {
		return Promise.reject('Refresh token not found');
	}

	const params = {
		query: {
			sessionId: refreshToken,
			access: accessToken,
		},
	};

	return httpClient()
		.invoke('user', 'renewToken', params)
		.then(
			(response) => {
				if (response) {
					return response.data.token.access;
				}

				return null;
			},
			(error) => {
				console.log(error);

				sessionStorage.removeItem('ACCESS_TOKEN');
				localStorage.removeItem('REFRESH_TOKEN');
			}
		);
};

const mapParameters = (url, parameters) => {
	const extra = [];
	let param;
	let newUrl;
	let result = url;

	if (parameters) {
		for (param in parameters) {
			// parâmetros que começam com "_" são usados para salvar estados dos filtros
			// e devem ser ignorados na chamada do serviço
			if (
				!param.startsWith('_') &&
				parameters[param] !== undefined &&
				url.indexOf(':' + param) < 0
			) {
				extra.push(param + '=' + encodeURIComponent(parameters[param]));
			}
		}

		newUrl = url.replace(/:(\w+)/g, (substring, match) => {
			parameters = parameters || {};

			let routeValue = parameters[match];
			if (!routeValue) {
				routeValue = ':' + match;
			}
			return routeValue;
		});

		// if we missed a value completely, then throw again
		if (newUrl.indexOf('/:') > 0) {
			throw 'Sushi: not all route values were matched (' + url + ')';
		}

		// finally attach query string parameters if necessary
		result = extra.length === 0 ? newUrl : newUrl + '?' + extra.join('&');
	}

	return result;
};

const httpClient = () => {
	const loadEndpointConfig = (category, operation) => {
		const endpointsConfig = JSON.parse(JSON.stringify(config));
		const endpointConfig = {};
		let found = false;

		for (const group in endpointsConfig) {
			if (
				endpointsConfig[group][category] &&
				endpointsConfig[group][category][operation]
			) {
				found = true;

				endpointConfig.baseUrl = endpointsConfig[group].baseUrl || '';
				endpointConfig.defaults = endpointsConfig[group].defaults || {};
				endpointConfig.endpoint = endpointsConfig[group][category][operation];

				break;
			}
		}

		if (!found) {
			throw (
				'Endpoint não encontrado para a categoria [' +
				category +
				'] e operação [' +
				operation +
				']'
			);
		}

		return endpointConfig;
	};

	const mountUrl = (category, operation, params) => {
		const endpointConfig = loadEndpointConfig(category, operation);
		let url = '';

		if (endpointConfig.baseUrl) {
			url = endpointConfig.baseUrl;
		} else {
			if (endpointConfig.endpoint.baseUrl) {
				url = endpointConfig.endpoint.baseUrl;
			}
		}

		if (endpointConfig.endpoint.url) {
			url += endpointConfig.endpoint.url;
		}

		url = mapParameters(url, params.query);

		return url;
	};

	const invoke = (category, operation, params = {}, formData = null) => {
		const endpointConfig = loadEndpointConfig(category, operation);
		const options = {};

		const url = mountUrl(category, operation, params);

		// global headers
		options.headers = endpointConfig.defaults.headers || {};

		// endpoint headers
		if (endpointConfig.endpoint.headers) {
			options.headers = Object.assign(
				options.headers,
				endpointConfig.endpoint.headers
			);
		}

		// operation headers
		if (params.headers) {
			options.headers = Object.assign(options.headers, params.headers);
		}

		for (const property in options.headers) {
			if (!options.headers[property]) {
				delete options.headers[property];
			}
		}

		// endpoint options
		if (endpointConfig.endpoint.options) {
			for (const i in endpointConfig.endpoint.options) {
				options[i] = endpointConfig.endpoint.options[i];
			}
		}

		// operation options
		if (params.options) {
			for (const i in params.options) {
				options[i] = params.options[i];
			}
		}

		if (formData) {
			options.data = formData;
		} else if (params.data) {
			options.data = params.data;
		}

		options.method = endpointConfig.endpoint.method;
		options.url = url;

		return axiosClient(options)
			.then((response) => {
				if (
					response.headers['content-type'] &&
					response.headers['content-type'].indexOf('application/json') < 0
				) {
					return response;
				}

				return response.data;
			})
			.catch((error) => {
				if (error.response && error.response.data) {
					if (
						[401, 419].indexOf(error.response.status) < 0 ||
						(error.response.status === 401 &&
							['ChangePassword', 'AppLogin'].indexOf(
								router.currentRoute.name || ''
							) >= 0)
					) {
						const message =
							error.response.data.message ||
							messagesApi.getMessageByCode(error.response.data.code);
						toaster.error(messagesApi.getMessage(message));
					}
				}

				throw error;
			});
	};

	return {
		invoke,
	};
};

export default httpClient();